import { defineStore } from 'pinia'
import { chatApi, type CreateChatPayload } from '@/modules/post-mvp/chat/api/chat'
import { useAuthSession } from '@/modules/auth/public'
import type { ChatParticipant, ChatSummary, Message } from '@/modules/post-mvp/chat/model/types'
import { isImageMimeType, isVisibleChatMessage, normalizeMessage } from '@/modules/post-mvp/chat/model/normalize'
import {
  appendOptimisticMessage,
  createOptimisticMessage,
  createTempMessageId,
  replaceOptimisticMessage,
  rollbackOptimisticMessage,
} from '@/modules/post-mvp/chat/model/optimisticSend'

export type { ChatParticipant, ChatSummary, Message, MessageAttachment } from '@/modules/post-mvp/chat/model/types'

export const useChatStore = defineStore('chat', {
  state: () => ({
    chats: [] as ChatSummary[],
    archivedChatCache: [] as ChatSummary[],
    messagesByChatId: new Map<number, Message[]>(),
    currentChat: null as ChatSummary | null,
    wsUpdates: null as unknown,
    pagination: {
      page: 1,
      limit: 20,
      total: 0,
      pages: 1,
    },
    // Ответы списка могут прийти не в том же порядке, в котором были отправлены
    // запросы (например, начальная загрузка страницы и сразу после неё поиск).
    // Последний начатый запрос — единственный, который вправе менять состояние.
    chatLoadRequestId: 0,
  }),

  getters: {
    currentUser(): { id: number; name: string } {
      const { user } = useAuthSession()
      return {
        id: user.value?.id ?? 0,
        name: user.value?.name ?? '',
      }
    },
    currentUserId(): number {
      return this.currentUser.id
    },
  },

  actions: {
    isImageMimeType(mimeType: string): boolean {
      return isImageMimeType(mimeType)
    },

    getChatById(chatId: number): ChatSummary | null {
      return this.chats.find((c) => c.id === chatId) || null
    },

    getMessagesByChatId(chatId: number): Message[] {
      return this.messagesByChatId.get(chatId) || []
    },

    clearMessages(chatId: number): void {
      this.messagesByChatId.set(chatId, [])
    },

    async loadChats(params?: {
      page?: number
      limit?: number
      type?: string
      search?: string
      sort?: string
      direction?: string
      archived?: boolean
    }): Promise<void> {
      const requestId = ++this.chatLoadRequestId
      const response = await chatApi.getChats(params)

      if (requestId !== this.chatLoadRequestId) {
        return
      }

      const envelope = response.data as {
        data?: {
          chats?: Array<{
            id: number
            name: string
            type: string
            context_type?: string
            context_id?: number
            is_archived?: boolean
            last_activity_at: string
            unread_count?: number
            is_muted?: boolean
            participant_count?: number
          }>
          pagination?: {
            page?: number
            limit?: number
            total?: number
            pages?: number
          }
        }
      }
      const payload = envelope.data ?? {}
      const items = Array.isArray(payload.chats) ? payload.chats : []
      const normalizedItems: ChatSummary[] = items.map((chat) => {
        const normalized: ChatSummary = {
          id: chat.id,
          name: chat.name,
          type: chat.type,
          is_archived: Boolean(chat.is_archived),
          last_activity_at: chat.last_activity_at,
          unread_count: chat.unread_count ?? 0,
          is_muted: chat.is_muted ?? false,
          participant_count: chat.participant_count ?? 0,
        }

        if (chat.context_type) {
          normalized.context_type = chat.context_type
        }
        if (typeof chat.context_id === 'number') {
          normalized.context_id = chat.context_id
        }

        return normalized
      })
      if (params?.archived) {
        const cached = this.archivedChatCache.filter(
          (cachedChat) => !normalizedItems.some((chat) => chat.id === cachedChat.id),
        )
        this.chats = [...normalizedItems, ...cached]
      } else {
        this.chats = normalizedItems.filter(
          (chat) => !this.archivedChatCache.some((cachedChat) => cachedChat.id === chat.id),
        )
      }
      if (payload.pagination) {
        this.pagination = {
          page: payload.pagination.page ?? 1,
          limit: payload.pagination.limit ?? 20,
          total: payload.pagination.total ?? 0,
          pages: payload.pagination.pages ?? 1,
        }
      }
    },

    async loadMessages(chatId: number, page = 1, limit = 50): Promise<void> {
      const response = await chatApi.getMessages(chatId, { page, limit })
      const payload = response.data
      const items = Array.isArray(payload.data) ? (payload.data as Record<string, unknown>[]) : []
      const visibleItems = items.filter(isVisibleChatMessage)
      this.messagesByChatId.set(
        chatId,
        visibleItems.map((item) => normalizeMessage(item, chatId)),
      )
    },

    async sendMessage(payload: {
      chatId: number
      content: string
      replyTo?: number
    }): Promise<void> {
      const trimmedContent = payload.content.trim()
      if (!trimmedContent) return

      const tempId = createTempMessageId()
      const optimistic = createOptimisticMessage({
        tempId,
        content: trimmedContent,
        authorId: this.currentUserId,
        authorName: this.currentUser.name,
        chatId: payload.chatId,
      })
      const currentMessages = this.getMessagesByChatId(payload.chatId)
      this.messagesByChatId.set(
        payload.chatId,
        appendOptimisticMessage(currentMessages, optimistic),
      )

      try {
        const response = await chatApi.sendMessage(payload.chatId, { content: trimmedContent })
        const messageRaw = response.data?.data as Record<string, unknown> | undefined
        const message = messageRaw ? normalizeMessage(messageRaw, payload.chatId) : undefined
        if (!message) {
          this.messagesByChatId.set(
            payload.chatId,
            rollbackOptimisticMessage(this.getMessagesByChatId(payload.chatId), tempId),
          )
          return
        }

        this.messagesByChatId.set(
          payload.chatId,
          replaceOptimisticMessage(this.getMessagesByChatId(payload.chatId), tempId, message),
        )
      } catch (error) {
        this.messagesByChatId.set(
          payload.chatId,
          rollbackOptimisticMessage(this.getMessagesByChatId(payload.chatId), tempId),
        )
        throw error
      }
    },

    async editMessage(messageId: number, content: string): Promise<void> {
      for (const [chatId, list] of this.messagesByChatId.entries()) {
        const idx = list.findIndex((m) => m.id === messageId)
        if (idx !== -1) {
          await chatApi.editMessage(chatId, messageId, { content })
          const updated = [...list]
          const current = updated[idx]
          if (!current) {
            break
          }
          updated[idx] = { ...current, content }
          this.messagesByChatId.set(chatId, updated)
          break
        }
      }
    },

    async deleteMessage(messageId: number): Promise<void> {
      for (const [chatId, list] of this.messagesByChatId.entries()) {
        const filtered = list.filter((m) => m.id !== messageId)
        if (filtered.length !== list.length) {
          await chatApi.deleteMessage(chatId, messageId)
          this.messagesByChatId.set(chatId, filtered)
          break
        }
      }
    },

    addMessage(message: Message & { chat_id?: number }): void {
      const chatId = message.chat_id || 1
      const list = this.getMessagesByChatId(chatId)
      this.messagesByChatId.set(chatId, [...list, message])
    },

    /**
     * Сообщение из Mercure; дедуп по id (своё приходит и POST, и SSE)
     */
    applyRealtimeMessage(chatId: number, raw: unknown): boolean {
      if (!Number.isInteger(chatId) || chatId <= 0) return false

      const message = normalizeMessage(raw, chatId)
      if (!Number.isInteger(message.id) || message.id <= 0) return false
      if (!isVisibleChatMessage(raw)) return false

      const list = this.getMessagesByChatId(chatId)
      if (list.some((existing) => existing.id === message.id)) return false

      if (!message.author.name) {
        const known = list.find((existing) => existing.author.id === message.author.id)
        if (known?.author.name) {
          message.author = { ...message.author, name: known.author.name }
        } else if (message.author.id === this.currentUserId) {
          message.author = { ...message.author, name: this.currentUser.name }
        }
      }

      this.messagesByChatId.set(chatId, [...list, message])
      return true
    },

    async uploadFile(chatId: number, file: File, description?: string): Promise<void> {
      await chatApi.uploadFile(chatId, file, description)
      await this.loadMessages(chatId)
    },

    async archiveChat(chatId: number): Promise<void> {
      await chatApi.archiveChat(chatId)
      const chat = this.getChatById(chatId)
      if (chat && !this.archivedChatCache.some((cachedChat) => cachedChat.id === chatId)) {
        this.archivedChatCache.unshift({ ...chat, is_archived: true })
      }
      this.chats = this.chats.filter((item) => item.id !== chatId)
      if (this.currentChat?.id === chatId) {
        this.currentChat = null
      }
    },

    async unarchiveChat(chatId: number): Promise<void> {
      await chatApi.unarchiveChat(chatId)
      this.archivedChatCache = this.archivedChatCache.filter((chat) => chat.id !== chatId)
      this.chats = this.chats.filter((chat) => chat.id !== chatId)
    },

    async leaveChat(chatId: number): Promise<void> {
      await chatApi.leaveChat(chatId)
      this.chats = this.chats.filter((c) => c.id !== chatId)
      if (this.currentChat?.id === chatId) {
        this.currentChat = null
      }
    },

    async searchChats(query: string): Promise<void> {
      await this.loadChats({ page: 1, search: query })
    },

    async createChat(data: {
      title?: string
      participants?: number[]
      name?: string
      type?: string
      description?: string
      contextType?: string
      contextId?: number
    }): Promise<ChatSummary> {
      const requestPayload: CreateChatPayload = {
        name: data.name ?? data.title ?? 'Новый чат',
        type: data.type ?? 'group',
      }

      if (data.description) {
        requestPayload.description = data.description
      }
      if (data.participants) {
        requestPayload.participants = data.participants
      }
      if (data.contextType) {
        requestPayload.context_type = data.contextType
      }
      if (typeof data.contextId === 'number') {
        requestPayload.context_id = data.contextId
      }

      const response = await chatApi.createChat(requestPayload)
      const payload = response.data as { data?: Record<string, unknown> }
      const raw = payload.data
      if (!raw || typeof raw !== 'object') {
        throw new Error('Invalid create chat response')
      }
      const chat: ChatSummary = {
        id: Number(raw['id']),
        name: String(raw['name'] ?? ''),
        type: String(raw['type'] ?? 'group'),
        is_archived: Boolean(raw['is_archived']),
        last_activity_at:
          typeof raw['last_activity_at'] === 'string'
            ? raw['last_activity_at']
            : new Date().toISOString(),
        unread_count: 0,
        is_muted: false,
        participant_count: Array.isArray(raw['participants'])
          ? raw['participants'].length
          : Number(raw['participant_count'] ?? 0),
      }
      if (typeof raw['context_type'] === 'string' && raw['context_type'] !== '') {
        chat.context_type = raw['context_type']
      }
      if (typeof raw['context_id'] === 'number') {
        chat.context_id = raw['context_id']
      }
      this.chats.push(chat)
      this.currentChat = chat
      return chat
    },

    async getChatParticipants(chatId: number): Promise<ChatParticipant[]> {
      const response = await chatApi.getParticipants(chatId)
      const payload = response.data as { data?: Array<Record<string, unknown>> }
      const items = Array.isArray(payload.data) ? payload.data : []
      return items.map((p) => {
        const user = (p['user'] as Record<string, unknown>) ?? {}
        return {
          id: Number(p['id']),
          user: {
            id: Number(user['id']),
            full_name: String(user['full_name'] ?? ''),
          },
          role: String(p['role'] ?? 'member'),
          is_online: Boolean(p['is_online']),
          last_seen_at:
            typeof p['last_seen_at'] === 'string' ? p['last_seen_at'] : new Date().toISOString(),
        }
      })
    },

    async removeParticipant(chatId: number, userId: number): Promise<void> {
      await chatApi.removeParticipant(chatId, userId)
      const participants = await this.getChatParticipants(chatId)
      const chat = this.getChatById(chatId)
      if (chat) {
        this.currentChat = { ...chat, participant_count: participants.length }
      }
    },
  },
})
